import { useState } from "react";
import { AppShell } from "../components/layout/AppShell";
import { DataTable, type Column } from "../components/ui/DataTable";
import { Badge, Skeleton } from "../components/ui/Primitives";
import { Button } from "../components/ui/Button";
import { ConfirmDialog, Modal } from "../components/ui/Modal";
import { SearchInput, Select, Input, Textarea } from "../components/ui/Fields";
import { usePagedList } from "../hooks/usePagedList";
import { apiRequest, ApiError } from "../api/client";
import { useToast } from "../components/ui/Toast";
import type { RoomBan, VoiceRoom } from "../api/types";

type Duration = "1" | "7" | "permanent" | "custom";

export function RoomsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"" | "live" | "ended">("");
  const { rows, isLoading, error, page, setPage, hasNextPage, reload } = usePagedList<VoiceRoom>("/admin/rooms", {
    search: search || undefined,
    status: status || undefined,
  });
  const [pendingEnd, setPendingEnd] = useState<VoiceRoom | null>(null);
  const [pinTarget, setPinTarget] = useState<{ room: VoiceRoom; pinOrder: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showToast } = useToast();

  // Round 33 points 13/14 — Room Ban / Unban. routes/admin/rooms.js's
  // banRoom/unbanRoom never call requireStepUp (unlike the user-ban
  // equivalent) — so this is a plain ConfirmDialog, same bar as Force End,
  // not StepUpDialog.
  const [banTarget, setBanTarget] = useState<VoiceRoom | null>(null);
  const [duration, setDuration] = useState<Duration>("permanent");
  const [customDays, setCustomDays] = useState("");
  const [banReason, setBanReason] = useState("");

  const [unbanTarget, setUnbanTarget] = useState<VoiceRoom | null>(null);

  const [historyTarget, setHistoryTarget] = useState<VoiceRoom | null>(null);
  const [roomBanHistory, setRoomBanHistory] = useState<RoomBan[] | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  const [editTarget, setEditTarget] = useState<VoiceRoom | null>(null);
  const [editForm, setEditForm] = useState({ title: "", announcement: "", roomAvatarUrl: "" });
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // Password protection — per the client's explicit requirement, the
  // admin panel only ever gets Reset/Set, Remove, and a yes/no
  // "protected?" status. There is no "view current password" field
  // anywhere here, and never will be — the Worker's own
  // GET /admin/rooms/:id/password response has no field that could hold
  // one even if this tried.
  const [passwordTarget, setPasswordTarget] = useState<VoiceRoom | null>(null);
  const [hasPassword, setHasPassword] = useState<boolean | null>(null);
  const [isLoadingPasswordStatus, setIsLoadingPasswordStatus] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [isSavingPassword, setIsSavingPassword] = useState(false);
  const [pendingRemovePassword, setPendingRemovePassword] = useState<VoiceRoom | null>(null);

  async function confirmEnd() {
    if (!pendingEnd) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/rooms/${pendingEnd.id}/end`, { method: "POST" });
      showToast("Room ended", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not end room", "error");
    } finally {
      setIsSubmitting(false);
      setPendingEnd(null);
    }
  }

  async function unpin(room: VoiceRoom) {
    try {
      await apiRequest(`/admin/rooms/${room.id}/pin`, { method: "POST", body: { pinned: false } });
      showToast("Unpinned", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not unpin", "error");
    }
  }

  async function confirmPin() {
    if (!pinTarget) return;
    setIsSubmitting(true);
    try {
      const pinOrder = pinTarget.pinOrder.trim() ? Number(pinTarget.pinOrder) : null;
      await apiRequest(`/admin/rooms/${pinTarget.room.id}/pin`, { method: "POST", body: { pinned: true, pinOrder } });
      showToast("Pinned", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not pin", "error");
    } finally {
      setIsSubmitting(false);
      setPinTarget(null);
    }
  }

  function openBanForm(room: VoiceRoom) {
    setDuration("permanent");
    setCustomDays("");
    setBanReason("");
    setBanTarget(room);
  }

  function durationDaysValue(): number | null {
    if (duration === "permanent") return null;
    if (duration === "custom") return Number(customDays);
    return Number(duration);
  }

  async function confirmBan() {
    if (!banTarget) return;
    if (duration === "custom" && (!customDays.trim() || Number(customDays) <= 0)) {
      showToast("Enter a valid number of days", "error");
      return;
    }
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/rooms/${banTarget.id}/ban`, {
        method: "POST",
        body: { reason: banReason.trim() || undefined, durationDays: durationDaysValue() },
      });
      showToast("Room banned", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not ban room", "error");
    } finally {
      setIsSubmitting(false);
      setBanTarget(null);
    }
  }

  async function confirmUnban() {
    if (!unbanTarget) return;
    setIsSubmitting(true);
    try {
      await apiRequest(`/admin/rooms/${unbanTarget.id}/unban`, { method: "POST" });
      showToast("Room unbanned", "success");
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not unban room", "error");
    } finally {
      setIsSubmitting(false);
      setUnbanTarget(null);
    }
  }

  function openHistory(room: VoiceRoom) {
    setHistoryTarget(room);
    setIsLoadingHistory(true);
    apiRequest<RoomBan[]>(`/admin/rooms/${room.id}/bans`)
      .then(setRoomBanHistory)
      .catch((err) => showToast(err instanceof ApiError ? err.message : "Could not load ban history", "error"))
      .finally(() => setIsLoadingHistory(false));
  }

  function openEditForm(room: VoiceRoom) {
    setEditForm({ title: room.title, announcement: "", roomAvatarUrl: "" });
    setEditTarget(room);
  }

  async function saveEdit() {
    if (!editTarget) return;
    setIsSavingEdit(true);
    try {
      await apiRequest(`/admin/rooms/${editTarget.id}`, {
        method: "PATCH",
        body: {
          title: editForm.title.trim() || undefined,
          announcement: editForm.announcement,
          roomAvatarUrl: editForm.roomAvatarUrl.trim() || null,
        },
      });
      showToast("Room updated", "success");
      setEditTarget(null);
      reload();
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not update room", "error");
    } finally {
      setIsSavingEdit(false);
    }
  }

  function openPasswordModal(room: VoiceRoom) {
    setNewPassword("");
    setHasPassword(null);
    setPasswordTarget(room);
    setIsLoadingPasswordStatus(true);
    apiRequest<{ hasPassword: boolean }>(`/admin/rooms/${room.id}/password`)
      .then((res) => setHasPassword(res.hasPassword))
      .catch((err) => showToast(err instanceof ApiError ? err.message : "Could not load password status", "error"))
      .finally(() => setIsLoadingPasswordStatus(false));
  }

  async function saveNewPassword() {
    if (!passwordTarget) return;
    if (newPassword.length < 4) {
      showToast("Password must be at least 4 characters", "error");
      return;
    }
    setIsSavingPassword(true);
    try {
      await apiRequest(`/admin/rooms/${passwordTarget.id}/password`, { method: "POST", body: { password: newPassword } });
      showToast("Room password set", "success");
      setNewPassword("");
      setHasPassword(true);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not set room password", "error");
    } finally {
      setIsSavingPassword(false);
    }
  }

  async function confirmRemovePassword() {
    if (!pendingRemovePassword) return;
    setIsSavingPassword(true);
    try {
      await apiRequest(`/admin/rooms/${pendingRemovePassword.id}/password`, { method: "DELETE" });
      showToast("Room password removed", "success");
      if (passwordTarget?.id === pendingRemovePassword.id) setHasPassword(false);
    } catch (err) {
      showToast(err instanceof ApiError ? err.message : "Could not remove room password", "error");
    } finally {
      setIsSavingPassword(false);
      setPendingRemovePassword(null);
    }
  }

  const columns: Column<VoiceRoom>[] = [
    { header: "Room ID", render: (row) => <span className="font-mono text-xs">{row.room_id}</span> },
    {
      header: "Title",
      render: (row) => (
        <div className="flex items-center gap-2">
          <span className="font-medium">{row.title}</span>
          {row.is_pinned && <Badge tone="gold">Pinned{row.pin_order != null ? ` #${row.pin_order}` : ""}</Badge>}
        </div>
      ),
    },
    { header: "Seats", render: (row) => row.max_seats },
    { header: "Status", render: (row) => (row.is_live ? <Badge tone="success">Live</Badge> : <Badge tone="neutral">Ended</Badge>) },
    { header: "Created", render: (row) => new Date(row.created_at).toLocaleString() },
    {
      header: "",
      className: "text-right",
      render: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => openEditForm(row)}>
            Edit
          </Button>
          <Button size="sm" variant="secondary" onClick={() => openPasswordModal(row)}>
            Password
          </Button>
          {row.is_pinned ? (
            <Button size="sm" variant="secondary" onClick={() => unpin(row)}>
              Unpin
            </Button>
          ) : (
            <Button size="sm" variant="secondary" onClick={() => setPinTarget({ room: row, pinOrder: "" })}>
              Pin
            </Button>
          )}
          {row.is_live && (
            <Button size="sm" variant="danger" onClick={() => setPendingEnd(row)}>
              Force End
            </Button>
          )}
          <Button size="sm" variant="danger" onClick={() => openBanForm(row)}>
            Ban
          </Button>
          <Button size="sm" variant="secondary" onClick={() => setUnbanTarget(row)}>
            Unban
          </Button>
          <Button size="sm" variant="ghost" onClick={() => openHistory(row)}>
            History
          </Button>
        </div>
      ),
    },
  ];

  return (
    <AppShell title="Voice Rooms">
      <p className="mb-4 text-xs text-ink-faint">
        Live Rooms on Home sorts pinned rooms first (by pin order, lower shows higher), then by real current seat
        occupancy — never a fake or client-calculated count.
      </p>
      <div className="mb-4 flex items-center gap-3">
        <SearchInput value={search} onChange={setSearch} placeholder="Search by title..." />
        <Select value={status} onChange={(e) => setStatus(e.target.value as typeof status)} className="w-auto">
          <option value="">All rooms</option>
          <option value="live">Live only</option>
          <option value="ended">Ended only</option>
        </Select>
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        keyField={(r) => r.id}
        isLoading={isLoading}
        error={error}
        onRetry={reload}
        emptyTitle="No rooms yet"
        pagination={{ page, pageSize: 20, hasNextPage, onPageChange: setPage }}
      />

      <ConfirmDialog
        isOpen={!!pendingEnd}
        onClose={() => setPendingEnd(null)}
        onConfirm={confirmEnd}
        isLoading={isSubmitting}
        isDangerous
        title={`End "${pendingEnd?.title}"?`}
        description="This immediately disconnects everyone in the room. This action is logged."
        confirmLabel="Force End"
      />

      <Modal
        isOpen={!!pinTarget}
        onClose={() => setPinTarget(null)}
        title={`Pin "${pinTarget?.room.title}"?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPinTarget(null)}>
              Cancel
            </Button>
            <Button onClick={confirmPin} isLoading={isSubmitting}>
              Pin
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            Pinned rooms always show above popularity on Home. If you pin more than one room, set an order to control
            which shows first (lower number = higher).
          </p>
          <Input
            label="Pin order (optional)"
            type="number"
            value={pinTarget?.pinOrder ?? ""}
            onChange={(e) => setPinTarget((p) => (p ? { ...p, pinOrder: e.target.value } : p))}
            hint="Leave blank if you're only pinning one room"
          />
        </div>
      </Modal>

      <Modal
        isOpen={!!banTarget}
        onClose={() => setBanTarget(null)}
        title={`Ban "${banTarget?.title}"?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setBanTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmBan} isLoading={isSubmitting}>
              Ban Room
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">This ends the room right now and blocks re-entry until the ban is lifted or expires.</p>
          <Select label="Duration" value={duration} onChange={(e) => setDuration(e.target.value as Duration)}>
            <option value="1">1 day</option>
            <option value="7">7 days</option>
            <option value="permanent">Permanent</option>
            <option value="custom">Custom</option>
          </Select>
          {duration === "custom" && (
            <Input label="Custom duration (days)" type="number" min={1} value={customDays} onChange={(e) => setCustomDays(e.target.value)} />
          )}
          <Textarea label="Reason (optional)" value={banReason} onChange={(e) => setBanReason(e.target.value)} rows={2} />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!unbanTarget}
        onClose={() => setUnbanTarget(null)}
        onConfirm={confirmUnban}
        isLoading={isSubmitting}
        title={`Unban "${unbanTarget?.title}"?`}
        description="Lifts every active ban on this room, allowing it to be joined and started again."
        confirmLabel="Unban"
      />

      <Modal isOpen={!!historyTarget} onClose={() => setHistoryTarget(null)} title={`Ban history — ${historyTarget?.title}`}>
        {isLoadingHistory ? (
          <Skeleton className="h-24 w-full" />
        ) : !roomBanHistory || roomBanHistory.length === 0 ? (
          <p className="text-sm text-ink-faint">No ban history on record.</p>
        ) : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {roomBanHistory.map((ban) => (
              <div key={ban.id} className="rounded-lg border border-surface-border p-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-ink">{ban.reason ?? "No reason given"}</p>
                  <Badge tone={ban.unbanned_at ? "neutral" : "danger"}>{ban.unbanned_at ? "Lifted" : "Active"}</Badge>
                </div>
                <p className="mt-1 text-xs text-ink-faint">
                  Banned {new Date(ban.banned_at).toLocaleString()} · {ban.expires_at ? `Expires ${new Date(ban.expires_at).toLocaleString()}` : "Permanent"}
                </p>
                {ban.unbanned_at && <p className="mt-0.5 text-xs text-ink-faint">Unbanned {new Date(ban.unbanned_at).toLocaleString()}</p>}
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal
        isOpen={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={`Edit "${editTarget?.title}"`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditTarget(null)}>
              Cancel
            </Button>
            <Button onClick={saveEdit} isLoading={isSavingEdit}>
              Save
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <Input label="Title" value={editForm.title} onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))} hint="18 characters or fewer" />
          <Textarea
            label="Announcement"
            value={editForm.announcement}
            onChange={(e) => setEditForm((f) => ({ ...f, announcement: e.target.value }))}
            rows={3}
          />
          <Input
            label="Room avatar URL"
            value={editForm.roomAvatarUrl}
            onChange={(e) => setEditForm((f) => ({ ...f, roomAvatarUrl: e.target.value }))}
          />
        </div>
      </Modal>

      <Modal
        isOpen={!!passwordTarget}
        onClose={() => setPasswordTarget(null)}
        title={`Room password — "${passwordTarget?.title}"`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setPasswordTarget(null)}>
              Close
            </Button>
            <Button onClick={saveNewPassword} isLoading={isSavingPassword} disabled={isLoadingPasswordStatus}>
              {hasPassword ? "Reset password" : "Set password"}
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          {isLoadingPasswordStatus ? (
            <Skeleton className="h-6 w-40" />
          ) : (
            <div className="flex items-center justify-between">
              <p className="text-sm text-ink-muted">
                Password protection: <Badge tone={hasPassword ? "success" : "neutral"}>{hasPassword ? "Enabled" : "Disabled"}</Badge>
              </p>
              {hasPassword && (
                <Button size="sm" variant="danger" onClick={() => setPendingRemovePassword(passwordTarget)}>
                  Remove password
                </Button>
              )}
            </div>
          )}
          <Input
            label="New password"
            type="text"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            hint="At least 4 characters. This replaces any existing password immediately — the stored password itself is never shown here."
            placeholder="Enter a new password to set or reset"
          />
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!pendingRemovePassword}
        onClose={() => setPendingRemovePassword(null)}
        onConfirm={confirmRemovePassword}
        isLoading={isSavingPassword}
        isDangerous
        title={`Remove password from "${pendingRemovePassword?.title}"?`}
        description="This clears password protection entirely — anyone will be able to enter the room without a password."
        confirmLabel="Remove password"
      />
    </AppShell>
  );
}
